import React, { FC } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@pisaka/ui';
import { FontCreatorType } from '../fontCreatorTypes';
import {
  useDownloadFont, useGenerateFont, usePayFont, useUpdateFont
} from './useFonts';
import { useModal } from '../../../../hooks/useModal';
import { PAYMENT_MODAL_NAME } from '../../../../constants/modalNamesConstants';

type PropsType = Pick<FontCreatorType, 'canDownloadFont' | 'canGenerateFont' | 'canPaymentFont' | 'symbols' | 'alphabetCountryCode' | 'fontId' | 'fontName'>;

const FontControlsContainer: FC<PropsType> = ({
  canGenerateFont, canPaymentFont, canDownloadFont, symbols, fontId, fontName
}: PropsType) => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const isNewPayment = searchParams.get('isNewPayment');

  let buttonLabel: string | undefined;
  let buttonOnClick: (() => void) | undefined;
  let buttonDisabled = false;
  let buttonLoading = false;

  const { isLoadingGenerateFont, generateFont } = useGenerateFont(fontId);
  const { isLoadingUpdateFont, updateFont } = useUpdateFont();
  const { isLoadingDownloadFont, downloadFont } = useDownloadFont();
  const { isLoadingPayFont, payFont } = usePayFont();

  const { onOpenModal } = useModal(PAYMENT_MODAL_NAME);

  const drawAlphabetsSymbols = symbols.filter(({ symbolSVGView, symbolSVGContent }) => symbolSVGView && symbolSVGContent);

  const hasGenerateFont = drawAlphabetsSymbols.length === symbols.length;

  if (canGenerateFont) {
    buttonLabel = t('fontCreator.generateFont');
    buttonOnClick = async () => {
      if (hasGenerateFont) {
        await generateFont();
      }
    };
    buttonDisabled = !hasGenerateFont;
    buttonLoading = isLoadingGenerateFont;
  }

  if (canPaymentFont) {
    if (isNewPayment) {
      buttonLabel = t('fontCreator.buyFont');
      buttonOnClick = () => onOpenModal({ fontId });
    } else {
      buttonLabel = t('fontCreator.buyFont');
      buttonOnClick = () => onOpenModal({ fontId });
      buttonLoading = isLoadingPayFont || isLoadingUpdateFont;
    }
  }

  if (canDownloadFont) {
    buttonLabel = t('fontCreator.downloadFont');
    buttonOnClick = async () => {
      const data = await downloadFont(fontId);

      if (data) {
        const url = window.URL.createObjectURL(new Blob([data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${fontName}.ttf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
    };
    buttonLoading = isLoadingDownloadFont;
  }

  if (!buttonLabel) return null;

  return (
    <Button
      size="sm"
      onClick={buttonOnClick}
      disabled={buttonDisabled || buttonLoading}
className="bg-gradient-accent hover:bg-accent shadow-accent text-primary-foreground"
    >
      {buttonLoading ? (
        <span className="flex items-center gap-2">
          <span className="h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent" />
          {buttonLabel}
        </span>
      ) : (
        buttonLabel
      )}
    </Button>
  );
};

export default FontControlsContainer;
